/**
 * Send → the customer gets their report link by text, from (249) 700-4555.
 *
 *   1. the link must open first: the sent report is in Supabase (public_report) — if it was sent with
 *      no signal it's shared again here, and the text waits until it is
 *   2. the text goes through "[C] App 7 - Texts" like any text from the app: it shows in the customer's
 *      thread, is noted on the Deal, and the link is saved on the Zoho Inspection (Report_Link)
 *
 * Queued first on Send (before the quotes) so the customer has it while the agent is still there —
 * the next-step screen waits for it. No signal at Send just means it goes out later.
 * A resend of the same inspection sends the new link with "updated" wording.
 */
import { getAccessToken, getSupabase } from './auth';
import { LIVE as TEXTS_LIVE, toE164 } from './communications';
import { shareSent } from './drafts';
import { LEADS_SOURCE } from './leads';
import { queueChange, registerZohoAction } from './sync';

const TEXTS_URL = process.env.REACT_APP_TEXTS_URL;
export const REPORT_TEXT_LIVE = LEADS_SOURCE === 'zoho' && TEXTS_LIVE;
const LINK_BASE = 'https://app.canadianinsulationexperts.com/r/';

export const reportLink = (token) => `${LINK_BASE}${token}`;

export const reportTextBody = ({ name, link, quotes = 1, resend = false }) => {
  const first = String(name || '').trim().split(/\s+/)[0];
  const what = `attic inspection report and quote${quotes === 1 ? '' : 's'}`;
  return [
    `Hi${first ? ` ${first}` : ''}, it's Canadian Insulation Experts. ${resend ? `Here's your updated ${what}` : `Your ${what} are ready`}: ${link}`,
    'Questions? Reply here or call 416-990-4555.',
  ].join('\n');
};

const noSignal = (message) => Object.assign(new Error(message), { offline: true });

const linkOpens = async (token) => {
  const supabase = getSupabase();
  if (!supabase) return false;
  const { data, error } = await supabase.rpc('public_report', { p_token: token });
  if (error) throw noSignal('No connection — the report text will go out when you’re back online.');
  return !!data;
};

export const pushReportText = async ({ inspectionId, draftId, token, phone, body }) => {
  if (!(await linkOpens(token))) {
    await shareSent(draftId).catch((err) => {
      throw noSignal(`The report isn’t online yet — ${err.message}`);
    });
    if (!(await linkOpens(token))) throw noSignal('The report link isn’t ready yet — will retry.');
  }
  const auth = await getAccessToken();
  if (!auth) throw new Error('Sign in again to text the customer.');
  let res;
  try {
    res = await fetch(TEXTS_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'send', to: phone, body, reportLink: reportLink(token), inspectionId }),
    });
  } catch {
    throw noSignal('No connection — the report text will go out when you’re back online.');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) {
    const err = new Error(out.error || `The report text wasn’t sent (${res.status}).`);
    err.offline = res.status >= 500 && !out.error;
    throw err;
  }
  return out;
};

registerZohoAction('text_report', (job) => pushReportText(job));

/**
 * Called on Send. Skipped in test mode, without a Zoho inspection, or without a mobile number.
 * A newer send for the same inspection replaces a text still waiting (only the latest link goes out).
 */
export const textReport = ({ inspectionId, draftId, token, phone, name, quotes, resend }) => {
  const to = toE164(phone);
  if (!REPORT_TEXT_LIVE || !inspectionId || !token) return { skipped: true };
  if (!to) return { skipped: true, why: 'No mobile number — the report wasn’t texted' };
  const body = reportTextBody({ name, link: reportLink(token), quotes, resend });
  const changeId = queueChange({
    type: 'report.text',
    leadId: inspectionId,
    label: `${name || 'Customer'} — report link texted`,
    payload: { to },
    zoho: { action: 'text_report', inspectionId, draftId, token, phone: to, body },
    supersede: `report-text:${inspectionId}`,
  });
  return { queued: true, changeId, to };
};
